import { Order, OrderFilters, CreateOrderRequest, UpdateOrderRequest } from '../../core/domain/entities/Order';
import { IOrderRepository } from '../../core/domain/repositories/IOrderRepository';
import { ApiService } from '../services/ApiService';

// Concrete Repository Implementation
export class OrderRepository implements IOrderRepository {
  constructor(private apiService: ApiService) {}

  async getAll(filters?: OrderFilters): Promise<Order[]> {
    const response = await this.apiService.getOrders();
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch orders');
    }
    
    let orders = response.data as Order[];
    
    // Apply filters
    if (filters) {
      if (filters.status) {
        orders = orders.filter(order => order.status === filters.status);
      }
      if (filters.customerId) {
        orders = orders.filter(order => order.customerId === filters.customerId);
      }
      if (filters.salesRepresentativeId) {
        orders = orders.filter(order => order.salesRepresentativeId === filters.salesRepresentativeId);
      }
      if (filters.dateFrom) {
        orders = orders.filter(order => {
          const orderDate = order.createdAt || order.created_at;
          return orderDate ? new Date(orderDate) >= new Date(filters.dateFrom!) : false;
        });
      }
      if (filters.dateTo) {
        orders = orders.filter(order => {
          const orderDate = order.createdAt || order.created_at;
          return orderDate ? new Date(orderDate) <= new Date(filters.dateTo!) : false;
        });
      }
    }
    
    return orders;
  }

  async getById(id: string): Promise<Order | null> {
    const response = await this.apiService.getOrder(id);
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch order');
    }
    
    // Backend'den gelen data'yı kontrol et
    console.log('getById response:', response);
    
    // Eğer data array ise ilk elemanı al, değilse direkt kullan
    const orderData = Array.isArray(response.data) ? response.data[0] : response.data;
    
    if (!orderData) {
      return null;
    }
    
    return orderData as Order;
  }

  async create(order: CreateOrderRequest): Promise<Order> {
    const response = await this.apiService.createOrder(order);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create order');
    }
    return response.data as Order;
  }

  async update(id: string, order: UpdateOrderRequest): Promise<Order> {
    const response = await this.apiService.updateOrder(id, order);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to update order');
    }
    return response.data as Order;
  }

  async delete(id: string): Promise<void> {
    const response = await this.apiService.deleteOrder(id);
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete order');
    }
  }

  async updateStatus(id: string, status: string, notes?: string): Promise<Order> {
    console.log('OrderRepository.updateStatus called with:', { id, status, notes });
    const response = await this.apiService.updateOrderStatus(id, status, notes);
    console.log('OrderRepository.updateStatus response:', response);
    if (!response.success || !response.data) {
      console.error('OrderRepository.updateStatus failed:', response.error);
      throw new Error(response.error || 'Failed to update order status');
    }
    console.log('OrderRepository.updateStatus returning:', response.data);
    return response.data as Order;
  }

  async getByUserRole(userId: string, role: string): Promise<Order[]> {
    const response = await this.apiService.getOrders();
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch orders');
    }
    
    const orders = response.data as Order[];
    
    // Filter by user role and ID
    return orders.filter(order => {
      if (role === 'sales_representative') {
        return order.salesRepresentativeId === userId;
      }
      return true; // For other roles, return all orders
    });
  }

  async getByStatus(status: string): Promise<Order[]> {
    const response = await this.apiService.getOrders();
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch orders');
    }
    
    const orders = response.data as Order[];
    return orders.filter(order => order.status === status);
  }
} 